import { api } from './api'
import {
  Prescription,
  CreatePrescriptionInput,
  UpdatePrescriptionInput,
  PrescriptionListResponse,
  AllergyCheckResult,
  InteractionCheckResult,
} from '../types/prescription'

export const prescriptionService = {
  /**
   * Create a new prescription (draft)
   */
  createPrescription: async (
    patientId: string,
    payload: CreatePrescriptionInput,
  ): Promise<Prescription> => {
    const response = await api.post<Prescription>(`/prescriptions`, {
      ...payload,
      patientId,
    })
    return response.data
  },

  /**
   * Get a prescription by ID
   */
  getPrescription: async (prescriptionId: string): Promise<Prescription> => {
    const response = await api.get<Prescription>(`/prescriptions/${prescriptionId}`)
    return response.data
  },

  /**
   * List prescriptions for a patient
   */
  listPatientPrescriptions: async (
    patientId: string,
    params?: { status?: string; prescriptionType?: string; scriptType?: string },
  ): Promise<PrescriptionListResponse> => {
    const response = await api.get<PrescriptionListResponse>(
      `/prescriptions/patient/${patientId}`,
      { params },
    )
    return response.data
  },

  /**
   * Update an existing prescription
   */
  updatePrescription: async (
    prescriptionId: string,
    payload: UpdatePrescriptionInput,
  ): Promise<Prescription> => {
    const response = await api.put<Prescription>(`/prescriptions/${prescriptionId}`, payload)
    return response.data
  },

  /**
   * Issue a prescription (change status from draft to issued)
   */
  issuePrescription: async (prescriptionId: string): Promise<Prescription> => {
    const response = await api.post<Prescription>(`/prescriptions/${prescriptionId}/issue`, {})
    return response.data
  },

  /**
   * Mark a prescription as printed
   */
  printPrescription: async (prescriptionId: string): Promise<Prescription> => {
    const response = await api.post<Prescription>(`/prescriptions/${prescriptionId}/print`, {})
    return response.data
  },

  /**
   * Reorder an existing prescription (create new prescription from existing)
   */
  reorderPrescription: async (prescriptionId: string): Promise<Prescription> => {
    const response = await api.post<Prescription>(`/prescriptions/${prescriptionId}/reorder`, {})
    return response.data
  },

  /**
   * Cancel a prescription
   */
  cancelPrescription: async (prescriptionId: string, reason?: string): Promise<Prescription> => {
    const response = await api.post<Prescription>(`/prescriptions/${prescriptionId}/cancel`, {
      reason,
    })
    return response.data
  },

  /**
   * Check patient allergies against medications
   */
  checkAllergies: async (
    patientId: string,
    medications: Array<{ name: string; referenceDrugId?: string; atcCodes?: string[] }>,
  ): Promise<AllergyCheckResult> => {
    const response = await api.post<AllergyCheckResult>(`/prescriptions/check-allergies`, {
      patientId,
      medications,
    })
    return response.data
  },

  /**
   * Check for drug interactions
   * If patientId is provided, also checks against current patient medications
   */
  checkDrugInteractions: async (
    medications: Array<{ name: string; referenceDrugId?: string; atcCodes?: string[] }>,
    patientId?: string,
  ): Promise<InteractionCheckResult> => {
    const response = await api.post<InteractionCheckResult>(`/prescriptions/check-interactions`, {
      medications,
      patientId,
    })
    return response.data
  },
}

